import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Partners = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const partners = [
    {
      name: 'NVIDIA Inception Program',
      logo: '/nvidia.png',
      description: 'AI Innovation Partner'
    },
    {
      name: 'Amazon Web Services',
      logo: '/amazon.png',
      description: 'Cloud Infrastructure Partner'
    },
    {
      name: 'Google Cloud',
      logo: '/cloud.png',
      description: 'Technology Partner'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % partners.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [partners.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % partners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + partners.length) % partners.length);
  };

  return (
    <div className="bg-gradient-to-br from-primary-600 to-primary-700 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            OUR PARTNERS
          </h2>
          <p className="mt-4 text-lg leading-8 text-primary-100">
            Collaborating with industry leaders to bring you the best AI technology
          </p>
        </motion.div>

        {/* Desktop View - All Partners */}
        <div className="hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="grid grid-cols-3 gap-8 items-center"
          >
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-full h-20 flex items-center justify-center mb-4">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {partner.name}
                  </h3>
                  <p className="text-sm text-primary-200">
                    {partner.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile/Tablet View - Carousel */}
        <div className="lg:hidden">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {partners.map((partner) => (
                  <div
                    key={partner.name}
                    className="w-full flex-shrink-0 bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-full h-20 flex items-center justify-center mb-4">
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {partner.name}
                      </h3>
                      <p className="text-sm text-primary-200">
                        {partner.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Controls */}
            <div className="flex justify-center items-center mt-6 space-x-4">
              <button
                onClick={prevSlide}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                aria-label="Previous partner"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Dots Indicator */}
              <div className="flex space-x-2">
                {partners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentSlide
                        ? 'bg-white'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                aria-label="Next partner"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Additional Partner Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">
              Partnership Excellence
            </h3>
            <p className="text-primary-200 max-w-3xl mx-auto">
              Our strategic partnerships with NVIDIA, AWS, and Google Cloud ensure that Speedr's 
              AI solutions are built on the most advanced and reliable infrastructure available. 
              This collaboration enables us to deliver cutting-edge offline AI technology that 
              meets the highest standards of performance and security.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <div className="flex items-center text-primary-200">
                <svg className="w-5 h-5 mr-2 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Advanced AI Infrastructure</span>
              </div>
              <div className="flex items-center text-primary-200">
                <svg className="w-5 h-5 mr-2 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Global Scale & Reach</span>
              </div>
              <div className="flex items-center text-primary-200">
                <svg className="w-5 h-5 mr-2 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Enterprise-Grade Security</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Partners;
